import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { supabase } from '../lib/supabase';
import { getOficinaId } from '../lib/office';
import { useAuth } from '../hooks/useAuth';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import { useLocalization } from '../hooks/useLocalization';
import { currency } from '../utils/formatters';
import AIAssistant from '../components/ai/AIAssistant';

function statusStyle(status) {
  const map = {
    aberta: ['rgba(249,115,22,0.14)', '#f97316'],
    diagnostico: ['rgba(6,182,212,0.14)', '#06b6d4'],
    aguardando_aprovacao: ['rgba(245,158,11,0.14)', '#f59e0b'],
    em_execucao: ['rgba(59,130,246,0.14)', '#3b82f6'],
    aguardando_peca: ['rgba(168,85,247,0.14)', '#a855f7'],
    finalizada: ['rgba(34,197,94,0.14)', '#22c55e'],
    entregue: ['rgba(34,197,94,0.14)', '#22c55e'],
    atrasada: ['rgba(239,68,68,0.14)', '#ef4444'],
  };

  const [background, color] =
    map[status] || ['rgba(113,113,122,0.14)', '#a1a1aa'];

  return { background, color };
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { settings } = useLocalization();
  const { perfil } = useAuth();
  const { features: planFeatures } = usePlanFeatures();

  const canViewFinance = perfil?.perfil === 'admin';
  const canViewOpportunities =
    canViewFinance && planFeatures.opportunityDashboard;
  const canUseAi = planFeatures.ai;

  const [loading, setLoading] = useState(true);
  const [showOnboardingDone, setShowOnboardingDone] = useState(false);

  const [dados, setDados] = useState({
    osAbertas: 0,
    osConcluidasMes: 0,
    osAtrasadas: 0,

    clientes: 0,
    veiculos: 0,
    temOSFinalizada: false,
    clientesInativos: 0,

    receitaMes: 0,
    receitaHoje: 0,
    ticketMedio: 0,

    oportunidadesValor: 0,
    oportunidadesCount: 0,

    contasReceber: 0,
    contasPagar: 0,

    ultimasOS: [],
    agendamentosHoje: [],
    receita30Dias: [],
    estoqueBaixo: [],
    lembretesContato: [],
  });

  useEffect(() => {
    async function load() {
      try {
        const oficinaId = await getOficinaId();

        if (!oficinaId) {
          return;
        }

        const hoje = new Date().toISOString().split('T')[0];

        const mesInicio = new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          1
        ).toISOString();

        const inicio30 = new Date();
        inicio30.setDate(inicio30.getDate() - 29);

        const financeiroMesQuery = canViewFinance
          ? supabase
              .from('financeiro')
              .select('valor,data')
              .eq('oficina_id', oficinaId)
              .eq('tipo', 'receita')
              .gte('data', mesInicio)
          : Promise.resolve({ data: [] });

        const financeiro30Query = canViewFinance
          ? supabase
              .from('financeiro')
              .select('valor,data')
              .eq('oficina_id', oficinaId)
              .eq('tipo', 'receita')
              .gte('data', inicio30.toISOString())
          : Promise.resolve({ data: [] });

        const [
          ordensRes,
          clientesRes,
          veiculosRes,
          financeiroMesRes,
          financeiro30Res,
          agendaRes,
          estoqueRes,
          lembretesRes,
        ] = await Promise.all([
          supabase
            .from('ordens_servico')
            .select('*, clientes(nome), veiculos(modelo, placa)')
            .eq('oficina_id', oficinaId)
            .order('data_entrada', { ascending: false }),

          supabase
            .from('clientes')
            .select('id', { count: 'exact', head: true })
            .eq('oficina_id', oficinaId)
            .is('removed_at', null),

          supabase
            .from('veiculos')
            .select('id', { count: 'exact', head: true })
            .eq('oficina_id', oficinaId)
            .is('removed_at', null),

          financeiroMesQuery,

          financeiro30Query,

          supabase
            .from('agendamentos')
            .select('*, clientes(nome), veiculos(modelo, placa)')
            .eq('oficina_id', oficinaId)
            .gte('data_hora', `${hoje}T00:00:00`)
            .lte('data_hora', `${hoje}T23:59:59`),

          supabase
            .from('estoque')
            .select('id,nome,quantidade,estoque_minimo')
            .eq('oficina_id', oficinaId),

          supabase
            .from('ordens_servico')
            .select(
              'id,numero,lembrete_contato_em,lembrete_contato_obs,clientes(nome,telefone)'
            )
            .eq('oficina_id', oficinaId)
            .not('lembrete_contato_em', 'is', null)
            .is('lembrete_contato_concluido_em', null)
            .lte('lembrete_contato_em', hoje)
            .order('lembrete_contato_em', { ascending: true })
            .limit(5),
        ]);

        const { data: summary } = canViewFinance
          ? await supabase
              .from('dashboard_executive_summary')
              .select('*')
              .eq('oficina_id', oficinaId)
              .maybeSingle()
          : { data: null };

        const ordens = ordensRes.data || [];

        const financeiroMesTotal = (financeiroMesRes.data || []).reduce(
          (sum, row) => sum + Number(row.valor || 0),
          0
        );

        const receitaHojeFallback = (financeiroMesRes.data || [])
          .filter((row) => String(row.data || '').startsWith(hoje))
          .reduce((sum, row) => sum + Number(row.valor || 0), 0);

        const ordensConcluidasFallback = ordens.filter((os) =>
          ['finalizada', 'entregue'].includes(
            String(os.status || '').toLowerCase()
          )
        ).length;

        const ticketFallback =
          financeiroMesTotal / Math.max(1, ordensConcluidasFallback);

        const receita30Dias = canViewFinance
          ? Array.from({ length: 30 }, (_, index) => {
              const dia = new Date(inicio30);
              dia.setDate(inicio30.getDate() + index);

              const key = dia.toISOString().split('T')[0];

              const receitaFinanceiro = (
                financeiro30Res.data || []
              )
                .filter((row) =>
                  String(row.data || '').startsWith(key)
                )
                .reduce(
                  (sum, row) => sum + Number(row.valor || 0),
                  0
                );

              return {
                dia: dia.toLocaleDateString(settings.locale, {
                  day: '2-digit',
                  month: '2-digit',
                  timeZone: settings.timeZone,
                }),
                receita: receitaFinanceiro,
              };
            })
          : [];

        setDados({
          osAbertas: Number(
            summary?.open_orders ??
              ordens.filter((os) =>
                [
                  'aberta',
                  'diagnostico',
                  'aguardando_aprovacao',
                  'em_execucao',
                  'aguardando_peca',
                ].includes(String(os.status || '').toLowerCase())
              ).length
          ),

          osConcluidasMes: Number(
            summary?.completed_orders_month ??
              ordensConcluidasFallback
          ),

          osAtrasadas: ordens.filter(
            (os) =>
              String(os.status || '').toLowerCase() ===
              'atrasada'
          ).length,

          clientes: Number(
            summary?.active_customers ??
              clientesRes.count ??
              0
          ),

          veiculos: Number(veiculosRes.count ?? 0),

          temOSFinalizada: ordens.some((os) =>
            ['finalizada', 'entregue'].includes(
              String(os.status || '').toLowerCase()
            )
          ),

          clientesInativos: Number(
            summary?.inactive_customers ?? 0
          ),

          receitaMes: Number(
            summary?.revenue_month ?? financeiroMesTotal
          ),

          receitaHoje: Number(
            summary?.revenue_today ?? receitaHojeFallback
          ),

          ticketMedio: Number(
            summary?.average_ticket_month ?? ticketFallback
          ),

          oportunidadesValor: canViewOpportunities
            ? Number(summary?.return_opportunity_value ?? 0)
            : 0,

          oportunidadesCount: canViewOpportunities
            ? Number(summary?.return_opportunity_count ?? 0)
            : 0,

          contasReceber: Number(
            summary?.accounts_receivable_open ?? 0
          ),

          contasPagar: Number(
            summary?.accounts_payable_open ?? 0
          ),

          ultimasOS: ordens.slice(0, 8),

          agendamentosHoje: agendaRes.data || [],

          receita30Dias,

          estoqueBaixo: (estoqueRes.data || []).filter(
            (item) =>
              Number(item.quantidade || 0) <=
              Number(item.estoque_minimo || 0)
          ),

          lembretesContato: lembretesRes.data || [],
        });
      } catch (error) {
        console.error('Erro dashboard:', error);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [
    canViewFinance,
    canViewOpportunities,
    settings.locale,
    settings.timeZone,
  ]);

  const primeirosPassos = [
    {
      label: 'Cadastre seu primeiro cliente',
      done: dados.clientes > 0,
      action: () => navigate('/novo-cliente'),
    },
    {
      label: 'Cadastre seu primeiro veículo',
      done: dados.veiculos > 0,
      action: () => navigate('/veiculos'),
    },
    {
      label: 'Crie sua primeira Ordem de Serviço',
      done: dados.ultimasOS.length > 0,
      action: () => navigate('/ordens/nova'),
    },
    {
      label: 'Finalize sua primeira Ordem de Serviço',
      done: dados.temOSFinalizada,
      action: () => navigate('/ordens-servico'),
    },
  ];

  const passosConcluidos = primeirosPassos.filter(
    (item) => item.done
  ).length;

  const progressoInicial = Math.round(
    (passosConcluidos / primeirosPassos.length) * 100
  );

  useEffect(() => {
    if (
      loading ||
      passosConcluidos !== primeirosPassos.length
    ) {
      return;
    }

    const identificador =
      perfil?.id || perfil?.email || 'usuario';

    const key =
      `mecsync-onboarding-completed-${identificador}`;

    if (!localStorage.getItem(key)) {
      setShowOnboardingDone(true);
      localStorage.setItem(key, '1');
    }
  }, [
    loading,
    passosConcluidos,
    primeirosPassos.length,
    perfil?.id,
    perfil?.email,
  ]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-zinc-400">
        Carregando dashboard...
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 text-zinc-100">
      <div>
        <h1 className="font-display text-3xl font-bold">
          Dashboard
        </h1>

        <p className="text-sm text-zinc-500">
          Operação em tempo real da oficina
        </p>
      </div>

      {passosConcluidos < primeirosPassos.length && (
        <div className="rounded-xl border border-orange-500/20 bg-[#111113] p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-semibold text-zinc-100">
                Primeiros passos no MecSync
              </h2>

              <p className="mt-1 text-sm text-zinc-400">
                Complete estas etapas para deixar sua oficina
                pronta para uso.
              </p>
            </div>

            <span className="text-sm font-semibold text-orange-400">
              {passosConcluidos}/{primeirosPassos.length}
            </span>
          </div>

          <div className="mb-5 h-2 overflow-hidden rounded-full bg-white/[0.06]">
            <div
              className="h-full rounded-full bg-orange-500 transition-all duration-300"
              style={{ width: `${progressoInicial}%` }}
            />
          </div>

          <div className="space-y-2">
            {primeirosPassos.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={
                  item.done ? undefined : item.action
                }
                disabled={item.done}
                className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
                  item.done
                    ? 'cursor-default border-green-500/10 bg-green-500/5 text-green-400'
                    : 'border-white/[0.06] bg-white/[0.02] text-zinc-300 hover:border-orange-500/30 hover:bg-orange-500/5'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${
                    item.done
                      ? 'border-green-500 bg-green-500 text-black'
                      : 'border-zinc-600 text-zinc-500'
                  }`}
                >
                  {item.done ? '✓' : ''}
                </span>

                <span
                  className={
                    item.done
                      ? 'line-through opacity-80'
                      : ''
                  }
                >
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          [
            'OS abertas',
            dados.osAbertas,
            'text-orange-400',
          ],

          canViewFinance
            ? [
                'Faturamento hoje',
                currency(dados.receitaHoje),
                'text-green-400',
              ]
            : [
                'Agendamentos hoje',
                dados.agendamentosHoje.length,
                'text-sky-400',
              ],

          canViewFinance
            ? [
                'Ticket medio',
                currency(dados.ticketMedio),
                'text-blue-400',
              ]
            : [
                'Clientes ativos',
                dados.clientes,
                'text-blue-400',
              ],

          [
            'Clientes inativos',
            dados.clientesInativos,
            'text-red-400',
          ],
        ].map(([label, value, tone]) => (
          <div
            key={label}
            className="rounded-lg border border-white/[0.06] bg-[#111113] p-5"
          >
            <div className="mb-2 text-xs uppercase tracking-wide text-zinc-500">
              {label}
            </div>

            <div
              className={`text-3xl font-extrabold ${tone}`}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      {canViewFinance && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[
            [
              'Faturamento do mês',
              currency(dados.receitaMes),
              'text-green-400',
            ],
            [
              'OS concluidas',
              dados.osConcluidasMes,
              'text-emerald-400',
            ],
            canViewOpportunities
              ? [
                  'Oportunidades',
                  currency(dados.oportunidadesValor),
                  'text-orange-400',
                ]
              : null,
            [
              'Receber / Pagar',
              `${currency(
                dados.contasReceber
              )} / ${currency(dados.contasPagar)}`,
              'text-sky-400',
            ],
          ]
            .filter(Boolean)
            .map(([label, value, tone]) => (
              <div
                key={label}
                className="rounded-lg border border-white/[0.06] bg-[#111113] p-5"
              >
                <div className="mb-2 text-xs uppercase tracking-wide text-zinc-500">
                  {label}
                </div>

                <div
                  className={`text-2xl font-extrabold ${tone}`}
                >
                  {value}
                </div>

                {label === 'Oportunidades' && (
                  <p className="mt-1 text-xs text-zinc-500">
                    {dados.oportunidadesCount} retorno(s) em
                    aberto
                  </p>
                )}
              </div>
            ))}
        </div>
      )}

      {canViewFinance && (
        <div className="rounded-lg border border-white/[0.06] bg-[#111113] p-5">
          <h2 className="mb-4 font-semibold">
            Receita dos últimos 30 dias
          </h2>

          <div className="h-64">
            {dados.receita30Dias.some(
              (row) => row.receita > 0
            ) ? (
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <LineChart data={dados.receita30Dias}>
                  <XAxis
                    dataKey="dia"
                    stroke="#71717a"
                    tick={{ fontSize: 11 }}
                  />

                  <YAxis
                    stroke="#71717a"
                    tick={{ fontSize: 11 }}
                  />

                  <Tooltip
                    formatter={(value) =>
                      currency(value)
                    }
                  />

                  <Line
                    type="monotone"
                    dataKey="receita"
                    stroke="#f97316"
                    strokeWidth={3}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-zinc-500">
                Nenhuma receita nos últimos 30 dias
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-3">
        <div className="rounded-lg border border-white/[0.06] bg-[#111113] p-5 xl:col-span-2">
          <h2 className="mb-4 font-semibold">
            Últimas 8 OS
          </h2>

          {dados.ultimasOS.length === 0 ? (
            <div className="py-10 text-center text-zinc-500">
              Nenhuma OS encontrada
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {dados.ultimasOS.map((os) => (
                    <tr
                      key={os.id}
                      className="border-t border-white/[0.06]"
                    >
                      <td className="py-3 pr-3 font-mono text-zinc-400">
                        #{os.numero}
                      </td>

                      <td className="py-3 pr-3">
                        {os.clientes?.nome || '-'}
                      </td>

                      <td className="py-3 pr-3 text-zinc-400">
                        {os.veiculos?.modelo ||
                          os.veiculos?.placa ||
                          '-'}
                      </td>

                      <td className="py-3 pr-3">
                        <span
                          className="rounded-full px-2.5 py-1 text-xs font-semibold"
                          style={statusStyle(os.status)}
                        >
                          {os.status || 'sem status'}
                        </span>
                      </td>

                      {canViewFinance && (
                        <td className="py-3 text-right font-semibold">
                          {currency(os.valor_total)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-4 xl:col-span-1">
          <div className="rounded-lg border border-white/[0.06] bg-[#111113] p-5">
            <h2 className="mb-4 font-semibold">
              Agendamentos de hoje
            </h2>

            {dados.agendamentosHoje.length === 0 ? (
              <p className="text-sm text-zinc-500">
                Nenhum agendamento para hoje
              </p>
            ) : (
              dados.agendamentosHoje.map((ag) => (
                <div
                  key={ag.id}
                  className="border-t border-white/[0.06] py-3 text-sm"
                >
                  <strong>
                    {ag.clientes?.nome}
                  </strong>

                  <span className="ml-2 text-zinc-500">
                    {ag.hora ||
                      new Date(
                        ag.data_hora
                      ).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                  </span>
                </div>
              ))
            )}
          </div>

          <div className="rounded-lg border border-white/[0.06] bg-[#111113] p-5">
            <h2 className="mb-4 font-semibold">
              Alertas
            </h2>

            {dados.osAtrasadas === 0 &&
            dados.estoqueBaixo.length === 0 &&
            dados.lembretesContato.length === 0 ? (
              <p className="text-sm text-zinc-500">
                Nenhum alerta no momento
              </p>
            ) : (
              <div className="space-y-2 text-sm">
                {dados.osAtrasadas > 0 && (
                  <p className="text-red-400">
                    {dados.osAtrasadas} OS atrasada(s)
                  </p>
                )}

                {dados.estoqueBaixo
                  .slice(0, 5)
                  .map((item) => (
                    <p
                      key={item.id}
                      className="text-yellow-400"
                    >
                      {item.nome}: {item.quantidade}/
                      {item.estoque_minimo}
                    </p>
                  ))}

                {dados.lembretesContato.map((item) => (
                  <p
                    key={item.id}
                    className="text-green-400"
                  >
                    Contatar{' '}
                    {item.clientes?.nome || 'cliente'}{' '}
                    sobre OS #{item.numero}
                  </p>
                ))}
              </div>
            )}
          </div>

          {canUseAi ? (
            <AIAssistant />
          ) : (
            <div className="rounded-lg border border-yellow-400/20 bg-yellow-400/5 p-5">
              <h2 className="font-semibold text-yellow-100">
                MecSync AI
              </h2>

              <p className="mt-2 text-sm leading-6 text-zinc-300">
                Assistente inteligente disponível no plano
                Premium.
              </p>
            </div>
          )}
        </div>
      </div>

      {showOnboardingDone && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-green-500/20 bg-[#111113] p-6 shadow-xl">
            <h2 className="text-xl font-bold text-zinc-100">
              Configuração inicial concluída 🎉
            </h2>

            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              Sua oficina já está pronta para utilizar o
              MecSync no dia a dia.
            </p>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() =>
                  setShowOnboardingDone(false)
                }
                className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-orange-400"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}